import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  fetchActiveFinancingScenarios,
  fetchScenarioFinancing,
} from "../../services/riskFinancingApi";

const DEFAULT_SCENARIO_STRATEGY = {
  premium: 0,
  deductible: 0,
  policy_limit: 0,
  coinsurance: 1,
};

export default function useScenarioFinancing({
  enabled,
  simulations,
}) {
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenarioId, setSelectedScenarioId] =
    useState("");

  const [strategy, setStrategy] = useState(
    DEFAULT_SCENARIO_STRATEGY
  );

  const [scenarioData, setScenarioData] =
    useState(null);

  const [scenariosLoading, setScenariosLoading] =
    useState(false);

  const [analysisLoading, setAnalysisLoading] =
    useState(false);

  const [scenariosError, setScenariosError] =
    useState(null);

  const [analysisError, setAnalysisError] =
    useState(null);

  const loadScenarios = useCallback(async () => {
    try {
      setScenariosLoading(true);
      setScenariosError(null);

      const data =
        await fetchActiveFinancingScenarios();

      const results = Array.isArray(data)
        ? data
        : [];

      setScenarios(results);

      return results;
    } catch (error) {
      console.error(
        "Unable to load FAIR scenarios:",
        error
      );

      setScenariosError(error);

      return [];
    } finally {
      setScenariosLoading(false);
    }
  }, []);

  const loadScenarioAnalysis = useCallback(
    async (
      scenarioId,
      currentStrategy
    ) => {
      if (!scenarioId) {
        setScenarioData(null);
        return null;
      }

      try {
        setAnalysisLoading(true);
        setAnalysisError(null);

        console.log(
          "Loading scenario financing:",
          {
            scenarioId,
            currentStrategy,
            simulations,
          }
        );

        const result =
          await fetchScenarioFinancing(
            scenarioId,
            currentStrategy,
            simulations
          );

        setScenarioData(result);

        return result;
      } catch (requestError) {
        console.error(
          "Scenario financing analysis error:",
          requestError
        );

        console.error(
          "Scenario financing response:",
          requestError?.response?.data
        );

        setAnalysisError(requestError);

        return null;
      } finally {
        setAnalysisLoading(false);
      }
    },
    [simulations]
  );

  const analyzeScenario = useCallback(
    async () => {
      return loadScenarioAnalysis(
        selectedScenarioId,
        strategy
      );
    },
    [
      loadScenarioAnalysis,
      selectedScenarioId,
      strategy,
    ]
  ); 

  useEffect(() => {
    if (!enabled) return;

    loadScenarios();
  }, [enabled, loadScenarios]);


  useEffect(() => {
    if (!enabled) return;
    if (!selectedScenarioId) return;
    if (scenariosLoading) return;

    loadScenarioAnalysis(
      selectedScenarioId,
      strategy
    );
  }, [
    enabled,
    selectedScenarioId,
    scenariosLoading,
    simulations,
    loadScenarioAnalysis,
  ]);

  const handleStrategyChange = useCallback(
    (field, value) => {
      setStrategy((previous) => ({
        ...previous,
        [field]: Number(value),
      }));
    },
    []
  );

  return {
    scenarios,
    selectedScenarioId,
    strategy,
    scenarioData,

    scenariosLoading,
    analysisLoading,

    scenariosError,
    analysisError,

    setSelectedScenarioId,
    handleStrategyChange,
    analyzeScenario,
  };
}